/**
 * 🎥 **화상 서비스 고르기** — 웨일온·구글밋·줌·Jitsi 를 한 자리에서 고른다.
 *
 * ## 왜 한 곳에 두나
 * 고르는 화면이 셋이다(선생님 「우리 반 화상 링크」·관리자 「행사 화상 무대」·
 * 관리자 「기본 회의 주소」). 화면마다 목록과 설명을 적으면 **아무 에러 없이 갈리고**,
 * 운영자는 「여기선 된다는데 저기선 안 된다」만 겪는다(이 저장소가 이미 당한 사고).
 * 그래서 **무엇을 고를 수 있는지·각각 뭐가 되는지**를 여기 한 벌만 둔다.
 *
 * ## 🔴 스펙은 **확인한 것만** 적는다
 * 인원·시간 제한을 보고 운영자가 **행사 인원을 정한다.** 지어낸 숫자를 적으면 행사 당일에
 * 사람이 못 들어오거나 통화가 끊긴다. 그래서 값마다 `source`(어디서 봤나)와
 * `checkedAt`(언제 봤나)을 같이 둔다 — 근거 없는 숫자는 아예 `null` 로 두고 화면이
 * 「확인 필요」라고 말한다. **모르는 것을 아는 척하는 게 더 나쁘다.**
 *
 * ## 🔴 「끼울 수 있나」는 여기 안 적는다
 * `MEET_SERVICES` 에 **실측값**으로 있다. 여기 또 적으면 두 곳이 갈린다 —
 * 구글밋을 「끼울 수 있다」고 잘못 적어 뒀다가 **영영 빈 iframe** 을 그린 적이 있다.
 */
import { MEET_SERVICES } from "./meetEmbed";

export type MeetKind = "whaleon" | "gmeet" | "zoom" | "jitsi" | "other";

/** 무료 기준 한도. **모르면 `null`** — 화면이 「확인 필요」라고 말한다. */
export interface MeetSpec {
  /** 한 회의 최대 참여 인원(무료 기준). */
  maxPeople: number | null;
  /**
   * 한 회의 시간 제한(분). `0` 은 **제한 없음**, `null` 은 **확인 못 함**.
   * 🔴 둘을 뭉개면 「제한 없음」과 「모름」이 같은 화면이 된다 — 정반대 결정을 부른다.
   */
  limitMinutes: number | null;
  /** 시간 제한에 붙는 단서(1:1 은 다르다 등). */
  limitNote?: string;
  /**
   * 🔴 인원·시간으로 안 잡히는 무료 한도. Jitsi 의 「달마다 접속 25개」가 여기다 —
   * 이걸 안 보이게 두면 「제한 없음」처럼 읽혀서 수업 둘째 주에 막힌다.
   */
  quota?: string;
  /** PC 에서 설치가 필요한가. 필요 없으면 `null`. */
  installPc: string | null;
  /** 참여자(아이·학부모)에게 계정이 필요한가. */
  guestNeedsAccount: boolean;
  /** 낯선 사람을 막는 수단. */
  lock: string;
  /** 어디서 봤나 — 근거 없는 숫자를 못 적게 한다. */
  source: string;
  /** 언제 봤나. 서비스 정책은 바뀐다 — 오래되면 다시 봐야 한다. */
  checkedAt: string;
}

export interface MeetKindInfo {
  id: MeetKind;
  emoji: string;
  label: string;
  /** 이 서비스로 알아볼 출처. 비면 「직접 넣기」다. */
  origins: readonly string[];
  /** 방을 만드는 곳. 없으면 `null`. */
  makeUrl: string | null;
  /** 칸에 보여 줄 예시. */
  placeholder: string;
  /** 언제 고르면 좋은가 — 한 줄. */
  goodFor: string;
  /** 대신 무엇을 감수하나 — 한 줄. 🔴 **빈 값 금지**(공짜 점심은 없다). */
  watchOut: string;
  /**
   * 🔴 **우리가 방 주소를 만들어 줄 수 있나.** 계정이 필요 없는 서비스만 된다(Jitsi).
   * 나머지는 로그인이 있어야 방이 생겨서 **자동화가 불가능**하다 — 서버가 로그인을
   * 흉내 내려면 비밀번호를 두어야 하고, 그건 불변식 1 과 충돌한다.
   */
  canGenerate: boolean;
  spec: MeetSpec;
  /** 방 만드는 법 — 화면이 번호를 붙여 그린다. */
  steps: readonly { title: string; body: string }[];
}

/** 웨일온 방 만들기 — 화면 셋이 같은 말을 하도록 여기 한 벌만. */
const WHALEON_HOW = [
  {
    /**
     * 🔴 **주소가 중요하다.** `whaleon.naver.com` 첫 화면은 **회의에 들어가는 화면**이라
     * 로그인이 안 보인다. 운영자가 실제로 「접속했는데 로그인 위치를 모르겠어」라고 했다.
     */
    title: "whaleon.naver.com/my 로 들어간다 (첫 화면 말고 여기!)",
    body:
      "그냥 whaleon.naver.com 만 치면 「회의 입장」 화면이 떠서 로그인이 안 보여요. " +
      "주소 끝에 /my 를 붙이면 네이버 로그인이 나옵니다. " +
      "방을 만드는 사람만 계정이 필요해요 — 아이들·학부모는 계정 없이 링크로 들어옵니다.",
  },
  {
    title: "「나의 회의」 → 「전용회의실」 을 만든다",
    body:
      "전용회의실은 **한 번 만들면 주소가 안 바뀌는 방**이에요. 수업이 열두 주라면 " +
      "열두 번 만들 필요 없이 이 방 하나면 됩니다.",
  },
  {
    title: "「회의 옵션」에서 「비밀번호」를 걸고 「대기실 사용」을 켠다",
    body:
      "아이들이 얼굴을 켜고 있는 방이에요. 비밀번호가 있으면 주소만 알아도 못 들어오고, " +
      "대기실을 켜면 선생님이 확인하고 들여보낼 수 있어요. " +
      "「호스트 없이 시작」은 꺼 두세요 — 켜면 선생님 없이 아이들만 방에 있게 됩니다.",
  },
  {
    title: "「회의 링크」를 복사한다 (「정보 복사」도 같아요)",
    body:
      "🔴 브라우저 **주소창의 주소가 아닙니다.** 방 안에서 복사한 링크여야 해요 — " +
      "주소창 주소를 넣으면 「유효하지 않은 회의 링크입니다」가 뜹니다. " +
      "복사한 링크는 whaleon.us 로 시작합니다(whaleon.naver.com 이 아니에요 — 맞는 거예요).",
  },
  {
    title: "여기에 붙여넣고 [🔎 확인] 을 누른다",
    body:
      "서버가 그 주소를 한 번 열어 보고 알려 드려요 — 없는 방인지, 우리 화면 안에서 " +
      "바로 열리는지. 좋으면 저장하세요.",
  },
] as const;

export const MEET_KINDS: readonly MeetKindInfo[] = [
  {
    id: "whaleon",
    emoji: "🐳",
    label: "네이버 웨일온",
    origins: ["https://whaleon.naver.com", "https://whaleon.us"],
    makeUrl: "https://whaleon.naver.com/my",
    placeholder: "https://whaleon.us/o/…",
    goodFor: "사람이 많고 오래 하는 행사. 우리 화면 안에서 열리는 유일한 쪽이에요.",
    /**
     * 🔴 운영자가 직접 겪었다(2026-09-03): 윈도우 크롬에서 「웨일 열기」를 눌러도
     * 아무 일도 안 일어났다. 그 화면엔 「회의 입장」이 없다.
     */
    watchOut: "PC 참여자는 웨일 브라우저를 받아야 해요 — 학교 PC는 설치가 막혀 있기도 해요.",
    canGenerate: false,
    spec: {
      maxPeople: 500,
      limitMinutes: 0,
      installPc: "웨일 브라우저 필요",
      guestNeedsAccount: false,
      lock: "비밀번호 · 대기실",
      source: "네이버 웨일온 정식 출시 발표(500명 · 시간 무제한)",
      checkedAt: "2026-09-03",
    },
    steps: WHALEON_HOW,
  },
  {
    id: "gmeet",
    emoji: "🟢",
    label: "구글 밋",
    origins: ["https://meet.google.com"],
    makeUrl: "https://meet.google.com",
    placeholder: "https://meet.google.com/abc-defg-hij",
    goodFor: "설치가 걸림돌일 때. 학교 PC·학부모 폰에서 그냥 열려요.",
    /**
     * 🔴 실측: `x-frame-options: DENY` — 우리 화면 안에서는 **절대** 안 열린다.
     * 「비어 보이면 새 창으로」로 덮을 일이 아니라 처음부터 새 창으로 연다.
     */
    watchOut: "우리 화면 안에서는 못 열려요(항상 새 창). 3명 넘으면 60분에서 끊겨요.",
    canGenerate: false,
    spec: {
      maxPeople: 100,
      limitMinutes: 60,
      limitNote: "1:1 은 24시간. 50분에 곧 끝난다는 알림이 떠요.",
      installPc: null,
      guestNeedsAccount: false,
      lock: "참가 승인(호스트가 들여보냄)",
      source: "https://support.google.com/meet/answer/7317473",
      checkedAt: "2026-09-03",
    },
    steps: [
      {
        title: "meet.google.com 에서 「새 회의」 → 「회의 만들기」",
        body: "구글 계정으로 로그인해요. 방을 만드는 사람만 계정이 필요합니다.",
      },
      {
        title: "「회의 링크 복사」를 누른다",
        body: "meet.google.com/abc-defg-hij 모양이에요. 이 링크는 계속 쓸 수 있어요.",
      },
      {
        title: "🔴 수업·행사 때 **먼저 들어가 있는다**",
        body:
          "구글밋은 비밀번호가 없어요. 대신 처음 보는 사람이 들어오려 하면 「참가 요청」이 " +
          "뜨고 호스트가 눌러야 들어옵니다 — 그러니 선생님이 먼저 들어가 계셔야 해요.",
      },
      {
        title: "여기에 붙여넣고 [🔎 확인] 을 누른다",
        body: "새 창으로 열립니다(구글이 끼워 넣기를 막아 둬서예요 — 우리가 못 바꿔요).",
      },
    ],
  },
  {
    id: "zoom",
    emoji: "🔵",
    label: "줌",
    origins: ["https://zoom.us", "https://us02web.zoom.us"],
    makeUrl: "https://zoom.us",
    placeholder: "https://us02web.zoom.us/j/1234567890",
    goodFor: "이미 줌을 쓰고 계실 때. 우리 화면 안에서도 열려요.",
    watchOut: "무료는 40분에서 끊겨요(1:1 도요). PC는 앱을 받으라고 계속 권해요.",
    canGenerate: false,
    spec: {
      maxPeople: 100,
      limitMinutes: 40,
      limitNote: "무료(Basic)는 1:1 도 40분이에요.",
      installPc: "앱 설치를 권함(브라우저로도 가능)",
      guestNeedsAccount: false,
      lock: "비밀번호 · 대기실",
      source: "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0067966",
      checkedAt: "2026-09-03",
    },
    steps: [
      { title: "zoom.us 에서 회의를 예약한다", body: "「예약」으로 만들면 주소가 안 바뀌어요." },
      {
        title: "🔴 「대기실」과 「암호」를 켠다",
        body: "아이들이 얼굴을 켜고 있는 방이에요. 둘 다 켜 두세요.",
      },
      { title: "「초대 복사」에서 회의 링크만 가져온다", body: "us02web.zoom.us/j/… 모양이에요." },
    ],
  },
  {
    id: "jitsi",
    emoji: "🟣",
    label: "Jitsi (계정 없이)",
    origins: ["https://meet.jit.si"],
    makeUrl: "https://meet.jit.si",
    placeholder: "https://meet.jit.si/greg-chick-…",
    goodFor: "계정도 설치도 없이 당장 한 번 열어 볼 때. 방 주소를 여기서 만들어 드려요.",
    /**
     * 🔴 **이게 제일 중요한 줄이다.** 약관을 실제로 읽어 보니 무료 이용은
     * 「달마다 접속 25개」다(사람 25명이 아니라 **접속 수 누적**). 아이 15명이 드나드는
     * 수업이면 **둘째 주에 막힌다.** 이 줄이 화면에 안 보이면 운영자는 「제한 없음」으로
     * 읽고 열두 주 수업을 짜다가 중간에 멈춘다.
     *
     * 우리가 겪은 「5분 뒤 끊김」도 여기서 온다 — 끼워 쓰는 것은 데모로만 허용된다.
     */
    watchOut: "무료는 **달마다 접속 25개**까지예요(수업엔 모자라요). 우리 화면 안에선 못 열려요.",
    canGenerate: true,
    spec: {
      maxPeople: null,
      limitMinutes: null,
      quota: "무료는 달마다 접속(Active End Point) 25개",
      installPc: null,
      guestNeedsAccount: false,
      lock: "방 안에서 비밀번호·대기실을 켤 수 있어요(기본은 꺼져 있어요)",
      source: "https://jitsi.org/meet-jit-si-terms-of-service/",
      checkedAt: "2026-09-03",
    },
    steps: [
      {
        title: "아래 [🎲 방 주소 만들기] 를 누른다",
        body:
          "계정이 필요 없어서 우리가 만들어 드릴 수 있어요. " +
          "🔴 이름을 짧게 바꾸지 마세요 — Jitsi 는 **이름만 맞히면 누구나 들어옵니다.**",
      },
      {
        title: "🔴 방에 먼저 들어가서 자물쇠를 건다",
        body:
          "방 안 「보안 옵션」에서 비밀번호를 걸고 「대기실」을 켜 주세요. 기본은 꺼져 있어요 — " +
          "안 걸면 주소를 아는 누구나 아이들 화면으로 들어옵니다.",
      },
      {
        title: "달마다 접속 25개를 넘기면 막힌다",
        body: "열두 주 수업엔 모자라요. 오래 쓰실 거면 웨일온이나 구글밋을 고르세요.",
      },
    ],
  },
  {
    id: "other",
    emoji: "✍️",
    label: "직접 넣기",
    origins: [],
    makeUrl: null,
    placeholder: "https://…",
    goodFor: "위에 없는 서비스를 쓰실 때. 주소만 맞으면 그대로 열려요.",
    watchOut: "우리가 모르는 곳이라 새 창으로만 열리고, 없는 방인지도 확인 못 해 드려요.",
    canGenerate: false,
    spec: {
      maxPeople: null,
      limitMinutes: null,
      installPc: null,
      guestNeedsAccount: false,
      lock: "그 서비스 설정을 따라요",
      source: "",
      checkedAt: "",
    },
    steps: [],
  },
];

/** 이 서비스를 우리 화면 **안에** 넣을 수 있나 — 실측값(`MEET_SERVICES`)에서 끌어온다. */
export function kindEmbeds(kind: MeetKind): boolean {
  const info = MEET_KINDS.find((k) => k.id === kind);
  if (!info || info.origins.length === 0) return false;
  return info.origins.every((o) => MEET_SERVICES.some((s) => s.origin === o && s.embed));
}

/** 붙여넣은 주소가 어느 서비스인가. 모르면 `"other"`. */
export function meetKindOf(url: string | undefined | null): MeetKind {
  const s = (url ?? "").trim();
  if (!s) return "other";
  let origin: string;
  try {
    origin = new URL(s).origin;
  } catch {
    return "other";
  }
  return MEET_KINDS.find((k) => k.origins.includes(origin))?.id ?? "other";
}

/** 이 갈래의 설명. 모르는 이름이면 「직접 넣기」로 떨어진다. */
export function meetKind(kind: MeetKind): MeetKindInfo {
  return MEET_KINDS.find((k) => k.id === kind) ?? MEET_KINDS[MEET_KINDS.length - 1]!;
}

/** 아무것도 안 넣었을 때 처음 보여 줄 갈래. */
export const MEET_KIND_DEFAULT: MeetKind = "whaleon";

/**
 * 🎥 **지금 화면이 보여 줄 갈래** — 붙여넣은 주소와 고른 것 중 무엇이 이기나.
 *
 * ## 🔴 여기서 한 번 틀렸다 (2026-09-03, 만든 날 바로)
 * 화면 셋이 각자 이렇게 짰다:
 *
 *     const kind = auto !== "other" ? auto : (picked ?? "whaleon");
 *
 * 그러면 **우리가 모르는 주소**를 넣었을 때(`auto === "other"`) 갈래가 웨일온으로
 * 떨어진다. 화면은 그 주소에 대고 **「500명 · 시간 제한 없음 · 우리 화면 안에서
 * 열려요」**라고 말한다 — 전부 사실이 아니다. 운영자는 그걸 믿고 100명짜리 행사를 연다.
 *
 * 규칙은 셋이다:
 *  ① 아는 서비스 주소가 들어 있으면 **그것**이 이긴다(고른 것보다).
 *  ② 주소가 있는데 모르는 곳이면 **「직접 넣기」** — 아는 척하지 않는다.
 *  ③ 칸이 비어 있을 때만 고른 것, 그것도 없으면 기본값.
 *
 * 🔴 판정을 화면마다 짜면 이렇게 갈린다. 그래서 한 곳으로 옮겼다.
 */
export function meetKindShown(
  /** 지금 칸에 들어 있는 주소. */
  url: string | undefined | null,
  /** 사람이 눌러서 고른 갈래(없으면 `null`). */
  picked: MeetKind | null,
): MeetKind {
  const typed = (url ?? "").trim();
  if (typed) {
    const auto = meetKindOf(typed);
    // ② 모르는 곳이면 「직접 넣기」 — 아는 서비스인 척하면 스펙까지 거짓이 된다
    return auto;
  }
  return picked ?? MEET_KIND_DEFAULT;
}

/**
 * 📋 **스펙 표 한 줄씩** — 화면 셋이 **같은 표**를 그리도록 문장까지 여기서 만든다.
 *
 * 🔴 화면에서 문장을 짜면 갈린다. 이 저장소는 그걸로 반나절을 썼다(관리자 미리보기 사고).
 */
export function meetSpecRows(kind: MeetKind): readonly { label: string; value: string }[] {
  const { spec } = meetKind(kind);
  const rows: { label: string; value: string }[] = [];
  // 🔴 「모른다」를 「제한 없다」로 보이게 하지 않는다 — 정반대 결정을 부른다
  rows.push({
    label: "최대 인원",
    value: spec.maxPeople === null ? "확인 필요" : `${spec.maxPeople}명`,
  });
  rows.push({
    label: "시간 제한",
    value:
      spec.limitMinutes === null
        ? "확인 필요"
        : spec.limitMinutes === 0
          ? "없음"
          : `${spec.limitMinutes}분`,
  });
  if (spec.limitNote) rows.push({ label: "시간 단서", value: spec.limitNote });
  if (spec.quota) rows.push({ label: "그 밖의 한도", value: spec.quota });
  rows.push({ label: "PC 설치", value: spec.installPc ?? "필요 없음" });
  rows.push({ label: "참여자 계정", value: spec.guestNeedsAccount ? "필요" : "필요 없음" });
  rows.push({ label: "낯선 사람 막기", value: spec.lock });
  rows.push({ label: "우리 화면 안에서", value: kindEmbeds(kind) ? "열려요" : "새 창으로 열려요" });
  return rows;
}

/**
 * 🎲 **Jitsi 방 주소를 만든다** — 계정이 없어서 우리가 만들 수 있는 유일한 쪽.
 *
 * 🔴 **이름을 길고 무작위로** 짓는다. Jitsi 는 이름만 맞히면 누구나 들어오는데,
 * 운영에 실제로 세 글자짜리 방이 들어가 있었다. 아이들이 얼굴을 켜는 방이다.
 * `eventRoomRisk` 가 요구하는 길이·숫자 섞기를 **반드시** 넘기도록 만든다.
 */
export function newJitsiRoom(rand: () => number = defaultRand): string {
  const abc = "abcdefghijkmnpqrstuvwxyz23456789"; // 헷갈리는 l·o·0·1 은 뺀다
  let tail = "";
  for (let i = 0; i < 10; i++) tail += abc[Math.floor(rand() * abc.length)];
  // 🔴 숫자가 **반드시** 하나는 들어가야 한다(무작위에 맡기면 가끔 하나도 안 나온다)
  return `greg-chick-${tail}${Math.floor(rand() * 10)}`;
}

/** 브라우저·워커·Node 어디에나 있는 안전한 난수. `Math.random` 은 안 쓴다(방 이름이다). */
function defaultRand(): number {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0]! / 2 ** 32;
}

/** 새 Jitsi 방 주소 한 개. */
export function newJitsiUrl(rand?: () => number): string {
  return `https://meet.jit.si/${newJitsiRoom(rand)}`;
}
